import {
  type DocumentsUploadResult,
  type UploadedDocuments,
  useUploadIdentityDocuments,
} from "Api/Mutations/Onboarding/useUploadIdentityDocuments";
import { DocumentsUploadForm } from "Components/Onboarding/shared/DocumentsUpload/DocumentsUploadForm/DocumentsUploadForm";
import {
  SecondDocumentTypes,
  type PersonalDocumentModel,
} from "Components/Onboarding/shared/DocumentsUpload/types";
import { ProcessingOcr } from "Components/Onboarding/shared/ProcessingOcr";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  onNextStepRequested: () => void;
  owningPartyPublicID: string;
  isSecondDocumentRequired?: boolean;
  /** The automated check declined a document: the step shows its panel instead of moving on. */
  onDeclined: (
    result: DocumentsUploadResult,
    documents: UploadedDocuments,
  ) => void;
};

export const DocumentsUpload = ({
  onNextStepRequested,
  owningPartyPublicID,
  isSecondDocumentRequired,
  onDeclined,
}: Props) => {
  const { t } = useResource();
  const { mutate: upload, isPending } = useUploadIdentityDocuments({
    // A failed upload moves on too: the documents are stored and the back office takes over, as after a failed OCR.
    onSettled: (result, _error, { documents }) => {
      if (result?.requiresManualReview) {
        onDeclined(result, documents);
      } else {
        onNextStepRequested();
      }
    },
  });

  if (isPending) {
    return <ProcessingOcr />;
  }

  if (!owningPartyPublicID) {
    return (
      <ErrorText>
        {t(Resources.Onboarding.Steps.Documents.NoOwningPartyError)}
      </ErrorText>
    );
  }

  const onSubmit = (data: PersonalDocumentModel) => {
    upload({
      documents: {
        firstIDCardBackSide: data.firstDocument.backSide,
        firstIDCardFrontSide: data.firstDocument.frontSide,
        secondIDCardFrontSide: data.secondDocument?.frontSide,
        secondIDCardBackSide:
          data.secondDocument?.documentType ===
          SecondDocumentTypes.DrivingLicense
            ? data.secondDocument?.backSide
            : undefined,
        secondDocumentType: data.secondDocument?.documentType,
      },
      partyPublicID: owningPartyPublicID,
    });
  };

  return (
    <DocumentsUploadForm
      onSubmit={onSubmit}
      isSecondDocumentRequired={isSecondDocumentRequired}
    />
  );
};
